import { api } from './client'

/**
 * TASK-0260 (F-006). Mirrors `CommissionRolesController.List`'s anonymous projection
 * (`{ Id, Name, SortOrder, PaysDifferential }`) — read-only in this task (P-11 seeds the 7 real
 * roles; no write path exists anywhere for this entity). `paysDifferential` backs the page's
 * read-only "Ruoli provvigionali" section (D-02).
 */
export interface CommissionRole {
  id: number
  name: string
  sortOrder: number
  paysDifferential: boolean
}

export function fetchCommissionRoles(): Promise<CommissionRole[]> {
  return api<CommissionRole[]>('/api/commission-roles')
}

/**
 * TASK-0260. Mirrors `CommissionPlanRatesController.List`'s anonymous projection. `percentage`,
 * `changeReason` and `createdBy` are ABSENT (not null/0) for a caller without `CanSeeRealAmounts` —
 * same redaction shape as `catalog.ts`'s `Product.commissionValue` (TASK-0116/F-005/F-006 closing
 * reviewer note (b): change reason and author are exactly as sensitive as the percentage itself).
 */
export interface CommissionPlanRate {
  id: number
  departmentId: number
  commissionRoleId: number
  percentage?: number
  validFrom: string
  validTo: string | null
  version: number
  status: string
  changeReason?: string | null
  createdBy?: number | null
}

/**
 * `GET /api/commission-plan-rates`. `asOf` (when given) must already be `yyyy-MM-dd` — the backend
 * rejects anything else with a 400 (F-005 reviewer note (d), closed by this task). With no `asOf`,
 * filtered to one (departmentId, roleId) pair, this doubles as that cell's full version history,
 * newest-first (the backend's own ordering) — the page's "storico versioni per cella".
 */
export function fetchCommissionPlanRates(params?: {
  departmentId?: number
  roleId?: number
  asOf?: string
}): Promise<CommissionPlanRate[]> {
  const qs = new URLSearchParams()
  if (params?.departmentId !== undefined) qs.set('departmentId', String(params.departmentId))
  if (params?.roleId !== undefined) qs.set('roleId', String(params.roleId))
  if (params?.asOf !== undefined) qs.set('asOf', params.asOf)
  const query = qs.toString()
  return api<CommissionPlanRate[]>(`/api/commission-plan-rates${query ? `?${query}` : ''}`)
}

/** Request body for `POST /api/commission-plan-rates/versions` — `changeReason` is mandatory
 * server-side (rejects null/empty/whitespace-only with a 400); `validFrom` must be `yyyy-MM-dd`. */
export interface CommissionPlanRateVersionRequest {
  departmentId: number
  commissionRoleId: number
  percentage: number
  validFrom: string
  changeReason: string
}

/**
 * TASK-0260. `POST /api/commission-plan-rates/versions` — Admin-only server-side
 * (`PolicyNames.Admin`); a non-Admin caller gets a 403 the `api()` helper turns into a thrown
 * `Error`. Never updates a row in place: closes the cell's current version (`ValidTo` = this
 * request's `validFrom`, left Active — never archived) and creates a new one. On a lost concurrency
 * race (two Admins on the same cell) the backend answers 409, surfaced here the same way as any
 * other non-2xx response.
 */
export function createCommissionPlanRateVersion(
  req: CommissionPlanRateVersionRequest,
): Promise<CommissionPlanRate> {
  return api<CommissionPlanRate>('/api/commission-plan-rates/versions', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

/**
 * TASK-0262 (F-008). Mirrors `CommissionAssignmentsController.List`'s anonymous projection.
 * `overridePercentage`/`overrideApprovedByUserId`/`overrideApprovedAt`/`overrideReason`,
 * `alternatePercentage`/`threshold`, `createdBy` and (new in this task) `effectivePercentage`/
 * `effectiveSource`/`effectiveAsOf` are ABSENT (not null/0) for a caller without
 * `CanSeeRealAmounts` — same redaction shape as `CommissionPlanRate` above (F-005/F-006 closing
 * reviewer note (b): "who changed it and why" is exactly as sensitive as the percentage itself,
 * and now so is the RESOLVED percentage).
 */
export interface UserCommissionAssignment {
  id: number
  userId: number
  departmentId: number
  commissionRoleId: number
  managerUserId: number | null
  overridePercentage?: number | null
  overrideApprovedByUserId?: number | null
  overrideApprovedAt?: string | null
  overrideReason?: string | null
  alternatePercentage?: number | null
  threshold?: number | null
  validFrom: string
  validTo: string | null
  createdBy?: number | null
  /**
   * D-01: the override wins when present, otherwise the listino resolved via
   * `CommissionPlanRateResolver` at `effectiveAsOf` (independent-review correction: the backend
   * used to resolve at this row's own `validFrom` always, which showed a stale percentage for an
   * assignment open since before the listino's last change) — `null` when the cell has no active
   * listino version (`effectiveSource === 'nessuna_versione_attiva'`) or the listino data is
   * internally inconsistent (`'listino_incoerente'`, two overlapping active versions for the same
   * cell).
   */
  effectivePercentage?: number | null
  effectiveSource?: 'override' | 'listino' | 'nessuna_versione_attiva' | 'listino_incoerente'
  /**
   * The calendar date actually used to resolve `effectivePercentage` against the listino: the
   * request's own `asOf` when given, otherwise "today" — CLAMPED into this row's own
   * `[validFrom, validTo)` window (a closed row is never resolved against a listino version that
   * only came into force after the row itself stopped applying).
   */
  effectiveAsOf?: string
}

/** `GET /api/commission-assignments`. `asOf` (when given) must already be `yyyy-MM-dd` — the
 * backend rejects anything else with a 400, same convention as `fetchCommissionPlanRates`. */
export function fetchCommissionAssignments(params?: {
  userId?: number
  departmentId?: number
  asOf?: string
}): Promise<UserCommissionAssignment[]> {
  const qs = new URLSearchParams()
  if (params?.userId !== undefined) qs.set('userId', String(params.userId))
  if (params?.departmentId !== undefined) qs.set('departmentId', String(params.departmentId))
  if (params?.asOf !== undefined) qs.set('asOf', params.asOf)
  const query = qs.toString()
  return api<UserCommissionAssignment[]>(`/api/commission-assignments${query ? `?${query}` : ''}`)
}

/** Request body for `POST /api/commission-assignments` — deliberately carries no override fields
 * (REQ-183-001: an override is always its own separate, dated, approved action, never bundled
 * into creation). `validFrom`/`validTo` must be `yyyy-MM-dd`. */
export interface CreateCommissionAssignmentRequest {
  userId: number
  departmentId: number
  commissionRoleId: number
  validFrom: string
  validTo?: string | null
  managerUserId?: number | null
  alternatePercentage?: number | null
  threshold?: number | null
}

/**
 * TASK-0262. `POST /api/commission-assignments` — Admin-only server-side; a non-Admin caller gets
 * a 403 the `api()` helper turns into a thrown `Error`. On an overlapping period for the same
 * (userId, departmentId) pair, or a lost concurrency race against the database's own EXCLUDE
 * constraint, the backend answers 409.
 */
export function createCommissionAssignment(
  req: CreateCommissionAssignmentRequest,
): Promise<UserCommissionAssignment> {
  return api<UserCommissionAssignment>('/api/commission-assignments', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

/**
 * TASK-0262. `POST /api/commission-assignments/{id}/close` — sets `validTo`, never deletes the
 * row. Admin-only server-side. On a lost `xmin` concurrency race (two Admins on the same row) the
 * backend answers 409.
 */
export function closeCommissionAssignment(
  id: number,
  validTo: string,
): Promise<UserCommissionAssignment> {
  return api<UserCommissionAssignment>(`/api/commission-assignments/${id}/close`, {
    method: 'POST',
    body: JSON.stringify({ validTo }),
  })
}

/**
 * TASK-0263 (F-009/P-18, REQ-018-004, REQ-058-012). Mirrors
 * `CommissionChainController.ToWireValue(PercentageSource)`'s wire values exactly — never the raw C#
 * enum name. `'listino_incoerente'` (TASK-0263 correction) is the SAME wire word
 * `UserCommissionAssignment.effectiveSource` already uses for the identical ambiguity (two
 * overlapping active listino versions) — deliberately distinct from `'nessuna'` (no active version at
 * all): F-008 drew that line on purpose, and this endpoint must not blur it back together.
 */
export type CommissionChainPercentageSource = 'override' | 'listino' | 'nessuna' | 'listino_incoerente'

/**
 * Mirrors `CommissionChainController.ToWireValue(ChainTruncationReason)`'s wire values exactly.
 * `'none'` means the walk reached the top of the chain naturally — every other value means the walk
 * was cut short and somebody who should have been considered was not (R-05). `'assegnazioni_incoerenti'`
 * (TASK-0263 correction) means two `UserCommissionAssignment` rows overlap for the same user and
 * department at the requested date — a data fault the backend refuses to arbitrate silently.
 */
export type CommissionChainTruncationReason =
  | 'none'
  | 'no_assignment_for_seller'
  | 'manager_without_assignment_in_department'
  | 'max_depth'
  | 'cycle'
  | 'missing_signed_at'
  | 'assegnazioni_incoerenti'

/**
 * One resolved link of the payout chain, from the seller (index 0) upwards. Mirrors
 * `CommissionChainController.Get`'s per-node anonymous projection. `percentage`, `alternatePercentage`,
 * `threshold` and `source` are ABSENT (not null) for a caller without `CanSeeRealAmounts` — same
 * redaction shape as `UserCommissionAssignment` above; `userId`/`commissionRoleId`/`paysDifferential`
 * (commercial-structure metadata) stay visible either way.
 */
export interface CommissionChainNode {
  userId: number
  commissionRoleId: number
  paysDifferential: boolean
  percentage?: number | null
  alternatePercentage?: number | null
  threshold?: number | null
  source?: CommissionChainPercentageSource
}

export interface CommissionChainResult {
  nodes: CommissionChainNode[]
  truncation: CommissionChainTruncationReason
}

/**
 * `GET /api/commission-chain` — walks `UserCommissionAssignment.ManagerUserId` from `userId` upwards
 * on `departmentId`, resolving each node's percentage as of `date`. `date` is MANDATORY and must
 * already be `yyyy-MM-dd`: the backend rejects a missing or malformed date with a 400 (REQ-058-012 —
 * the chain and its percentages are only meaningful at a specific date, never "today" by default).
 */
export function fetchCommissionChain(params: {
  userId: number
  departmentId: number
  date: string
}): Promise<CommissionChainResult> {
  const qs = new URLSearchParams()
  qs.set('userId', String(params.userId))
  qs.set('departmentId', String(params.departmentId))
  qs.set('date', params.date)
  return api<CommissionChainResult>(`/api/commission-chain?${qs.toString()}`)
}

/**
 * TASK-0264 (F-010, P-14 — REQ-018-003). Mirrors `CommissionOperatorProfilesController.List`'s
 * anonymous projection. `extraEmail`/`extraRid` are ABSENT (not null/0) for a caller without
 * `CanSeeRealAmounts` — same redaction shape as `CommissionPlanRate.percentage` (F-005/F-006 closing
 * reviewer note (b)). One row per `operatorId` (backend unique index) — no version history, unlike
 * `CommissionPlanRate`/`CommissionBracket` below.
 */
export interface CommissionOperatorProfile {
  id: number
  operatorId: number
  bracketMeasure: 'AnnualConsumptionKwh' | 'PowerKw' | 'ContractCountInPeriod'
  extraEmail?: number
  extraRid?: number
  createdBy?: number | null
}

/** `GET /api/commission-operator-profiles`, optionally filtered by `operatorId`. Read-only — no
 * write path exists anywhere for this entity yet. */
export function fetchCommissionOperatorProfiles(params?: {
  operatorId?: number
}): Promise<CommissionOperatorProfile[]> {
  const qs = new URLSearchParams()
  if (params?.operatorId !== undefined) qs.set('operatorId', String(params.operatorId))
  const query = qs.toString()
  return api<CommissionOperatorProfile[]>(`/api/commission-operator-profiles${query ? `?${query}` : ''}`)
}

/**
 * TASK-0264 (F-010, P-19a — REQ-018-003). Mirrors `CommissionBracketsController.List`'s anonymous
 * projection. `amount` is ABSENT (not null/0) for a caller without `CanSeeRealAmounts` — same
 * redaction shape as `CommissionPlanRate.percentage`. `fromValue`/`toValue` are the bracket's own
 * measured-quantity range and are ALWAYS INCLUSIVE on both ends (unlike `validFrom`/`validTo` below,
 * which stay this module's usual EXCLUSIVE-on-`validTo` temporal window) — `toValue` is `null` for an
 * open-ended top bracket (the legacy package's own real "500.001 to ∞" row). `measure` is this row's
 * OWN measure and is never guaranteed to match the operator's own
 * `CommissionOperatorProfile.bracketMeasure` — see the backend entity's own remarks (fact 2: three
 * legacy operators declare a profile measure that disagrees with every one of their real bracket
 * rows).
 */
export interface CommissionBracket {
  id: number
  operatorId: number
  segment: 'business' | 'consumer'
  commodity: 'energia' | 'gas'
  measure: 'AnnualConsumptionKwh' | 'PowerKw' | 'ContractCountInPeriod'
  fromValue: number
  toValue: number | null
  amount?: number
  validFrom: string
  validTo: string | null
  version: number
  status: string
  createdBy?: number | null
}

/** `GET /api/commission-brackets`. `asOf` (when given) must already be `yyyy-MM-dd` — the backend
 * rejects anything else with a 400, same convention as `fetchCommissionPlanRates`. Filters on
 * `segment`/`commodity` are exact matches against the backend's own closed vocabulary. */
export function fetchCommissionBrackets(params?: {
  operatorId?: number
  segment?: string
  commodity?: string
  asOf?: string
}): Promise<CommissionBracket[]> {
  const qs = new URLSearchParams()
  if (params?.operatorId !== undefined) qs.set('operatorId', String(params.operatorId))
  if (params?.segment !== undefined) qs.set('segment', params.segment)
  if (params?.commodity !== undefined) qs.set('commodity', params.commodity)
  if (params?.asOf !== undefined) qs.set('asOf', params.asOf)
  const query = qs.toString()
  return api<CommissionBracket[]>(`/api/commission-brackets${query ? `?${query}` : ''}`)
}

/**
 * TASK-0265 (F-011, P-19b — REQ-018-003). Mirrors `CommissionBracketsController.Resolve`'s wire
 * shape exactly:
 * - `'matched'`: exactly one active bracket covers `value` on `date` — `bracketId`/`fromValue`/
 *   `toValue`/`extrasApplied` are always present (structural metadata, never an amount);
 *   `bracketAmount`/`amount` are ABSENT (not null) for a caller without `CanSeeRealAmounts`, same
 *   redaction shape as `CommissionBracket.amount` above. `amount` already has whichever of
 *   `hasEmailExtra`/`hasRidExtra` were requested summed in (fix #6) — `bracketAmount` is the bare
 *   bracket figure.
 * - `'no_match'`: no active bracket covers the value on that date — never a silent 0. Reachable both
 *   for "this cell has no brackets at all" and for a value inside one of the legacy data's own
 *   fractional gaps (e.g. WEKIWI 4.999 -> 5.000) — the backend deliberately does not distinguish the
 *   two (see `CommissionBracketResolutionOutcome.NoMatch`'s own remarks).
 * - `'incoherent'`: two or more active brackets overlap on this exact (segment, commodity, measure,
 *   value, date) — a data-integrity fault the backend refuses to arbitrate (same "never guess"
 *   convention as `'listino_incoerente'`/`'assegnazioni_incoerenti'` elsewhere in this module),
 *   surfaced here as a `200` with `error` naming every offending row, never a `500`/`409`.
 *
 * TASK-0265 correction (independent reviewer, BLOCKING finding): a request for an extra with no
 * `CommissionOperatorProfile` configured for the operator is now a `400` (see
 * `resolveCommissionBracket`'s own doc comment) — a missing profile row is never silently treated as
 * "extras are zero". `extrasApplied` reports, for a `'matched'` outcome, which of the two extras were
 * ACTUALLY summed into `amount` — self-describing even when a requested extra is genuinely configured
 * as zero, a case that would otherwise be indistinguishable from "never applied" by comparing
 * `bracketAmount` to `amount` alone.
 */
export interface CommissionBracketResolution {
  outcome: 'matched' | 'no_match' | 'incoherent'
  id?: number
  operatorId?: number
  fromValue?: number
  toValue?: number | null
  bracketAmount?: number
  amount?: number
  extrasApplied?: { email: boolean; rid: boolean }
  error?: string
}

/**
 * `GET /api/commission-brackets/resolve` — the "Prova scaglione" diagnostic endpoint. Gated to
 * `EqualStaff` server-side (narrower than every other GET in this module, which only requires
 * `Authenticated`): a non-staff caller gets a 403 the `api()` helper turns into a thrown `Error`.
 * `value` and `date` are both mandatory, hand-parsed server-side with an invariant decimal point /
 * strict ISO `yyyy-MM-dd` respectively — anything else is a 400, same as an invalid `segment`/
 * `commodity`/`measure` (deliberately UNLIKE `fetchCommissionBrackets`'s own filters, which just
 * return an empty list for an unrecognised value: this endpoint exists so staff can TRY one input,
 * so a typo is rejected outright rather than silently resolving to `'no_match'`). Also a `400`
 * (TASK-0265 correction) when `hasEmailExtra`/`hasRidExtra` asks for an extra but no
 * `CommissionOperatorProfile` exists for `operatorId` — no profile is imported for ANY operator
 * today, so this is the everyday path, not an edge case, for a caller ticking either checkbox before
 * a profile row exists — the `api()` helper turns it into a thrown `Error` the caller must surface,
 * never a `'matched'` result with the extra silently dropped.
 */
export function resolveCommissionBracket(params: {
  operatorId: number
  segment: string
  commodity: string
  measure: string
  value: string
  date: string
  hasEmailExtra?: boolean
  hasRidExtra?: boolean
}): Promise<CommissionBracketResolution> {
  const qs = new URLSearchParams()
  qs.set('operatorId', String(params.operatorId))
  qs.set('segment', params.segment)
  qs.set('commodity', params.commodity)
  qs.set('measure', params.measure)
  qs.set('value', params.value)
  qs.set('date', params.date)
  if (params.hasEmailExtra !== undefined) qs.set('hasEmailExtra', String(params.hasEmailExtra))
  if (params.hasRidExtra !== undefined) qs.set('hasRidExtra', String(params.hasRidExtra))
  return api<CommissionBracketResolution>(`/api/commission-brackets/resolve?${qs.toString()}`)
}

/**
 * TASK-0262 (REQ-183-001). `POST /api/commission-assignments/{id}/override` — records a dated,
 * approved override on top of the listino (D-01); never modifies `commission_plan_rates`. The
 * approving Admin and the approval timestamp are ALWAYS resolved server-side from the
 * authenticated caller — never sent by this client. `overrideReason` is mandatory server-side
 * (rejects null/empty/whitespace-only with a 400). On a lost `xmin` concurrency race the backend
 * answers 409.
 */
export function overrideCommissionAssignment(
  id: number,
  req: { overridePercentage: number; overrideReason: string },
): Promise<UserCommissionAssignment> {
  return api<UserCommissionAssignment>(`/api/commission-assignments/${id}/override`, {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

/**
 * TASK-0267 (F-013, P-21a — REQ-018-004, REQ-058-012, REQ-019-001). One node of the resolved
 * commission chain, from the seller (level 0) upward, with BOTH percentages the backend exposes
 * (`CommissionPreviewController.cs`'s own remarks on why the two must never be conflated):
 * `grossPercentage` (this node's OWN declared percentage — what it carries upward to the node
 * above) and `netPercentage` (what it actually applies/gets paid). Both are `null` for a
 * pass-through node (no listino row at all for its role) — a DIFFERENT, more absent state than a
 * differential/threshold node that resolved to a real but zero net share (`netPercentage: 0`,
 * `amount: 0`).
 */
export interface CommissionPreviewChainNode {
  userId: number
  commissionRoleId: number
  level: number
  paysDifferential: boolean
  percentageSource: 'override' | 'listino' | 'nessuna' | 'listino_incoerente'
  grossPercentage: number | null
  netPercentage: number | null
  amount: number | null
}

/**
 * `GET /api/contracts/{id}/commission-preview`'s full response shape — ALWAYS a `200` for a
 * visible, in-scope contract (TASK-0267 review correction: this module's own consolidated
 * convention for a diagnostic GET is an explicit outcome inside a `200`, never a `4xx` for a
 * "not computable right now" state — see `outcome`/`base.source`/`chain.truncation` below).
 *
 * `outcome` is `'ok'` for a normal computation, or `'signed_at_mancante'` when
 * `Contract.SignedAt` is null — in that case `signedAt`/`planDate` are `null`, `base.source` is
 * also `'signed_at_mancante'` (with the explanatory message in `base.note`), `chain.truncation` is
 * `'missing_signed_at'` with zero nodes, and `totals` is `null`. No fallback to any other date on
 * the contract is ever attempted (REQ-058-012).
 *
 * `base.source` is otherwise one of `'frozen'` (`Contract.CommissionBasis`, already calculated and
 * locked in), `'product_rule'` (F-004's `BasisAmount`, computed on the fly with no
 * contract-specific condition attributes), `'product_rule_negative'` (F-004's `BasisAmount` came
 * back negative — a real, reachable state for a product with a large negative additive
 * `ProductRule` — never handed to the split, see `base.note` for the actual value),
 * `'bracket_not_computable'` (the operator prices by scaglione per F-011, but this contract does
 * not expose the commodity/segment/measured value the resolver needs — see `base.note`) or
 * `'not_determinable'` — never a number with no declared source.
 *
 * `chain.truncation` is `'none'` when the walk reached the top naturally; every other value
 * (including `'missing_signed_at'` above and the two this endpoint adds on top of
 * `CommissionChainResolver`'s own vocabulary, `'no_agent'`/`'no_department'`) means somebody who
 * should have been considered was not, or nothing was resolvable at all. `totals` is `null`
 * whenever `base.amount` is `null` or the chain resolved zero nodes — nothing to split.
 */
export interface CommissionPreview {
  contractId: number
  outcome: 'ok' | 'signed_at_mancante'
  signedAt: string | null
  planDate: string | null
  base: { source: string; amount: number | null; note: string | null }
  chain: { truncation: string; nodes: CommissionPreviewChainNode[] }
  totals: { totalPercentage: number; companyResidual: number; chainTruncated: boolean } | null
}

/**
 * Gated server-side to Admin/EqualStaff WITH `CanSeeRealAmounts` and row-level visibility on the
 * contract (ADR-008) — an Agent-tier caller, or one without the flag, gets a 403 the `api()` helper
 * turns into a thrown `Error`; an invisible/nonexistent contract gets a 404 the same way. Every
 * other outcome, INCLUDING a missing `SignedAt`, is a `200` — see `CommissionPreview`'s own doc
 * comment for the full `outcome`/`base.source`/`chain.truncation` vocabulary callers should render,
 * never treated as a thrown error.
 */
export function fetchCommissionPreview(contractId: number): Promise<CommissionPreview> {
  return api<CommissionPreview>(`/api/contracts/${contractId}/commission-preview`)
}

/**
 * TASK-0268 (F-014, P-21b — REQ-019-002, REQ-016-001). One row of `GET
 * /api/contracts/{id}/commission-runs`'s list — a calculation ATTEMPT that actually happened and was
 * PERSISTED (unlike `CommissionPreview` above, which leaves no trace). `basisAmount`/`totalPercentage`/
 * `totalAmount`/`companyResidual` are ABSENT (not null) for a caller without `CanSeeRealAmounts` — same
 * redaction shape as every other money-carrying list in this module; `inputFingerprint`/`basisSource`/
 * `planDate`/`truncation`/`marginStatus` stay visible regardless (process metadata, not an amount).
 */
export interface CommissionRunSummary {
  id: number
  contractId: number
  runAt: string
  triggeredByUserId: number | null
  inputFingerprint: string
  basisAmount?: number
  basisSource: string
  planDate: string
  totalPercentage?: number
  totalAmount?: number
  companyResidual?: number
  truncation: string | null
  marginStatus: string | null
}

/** `GET /api/contracts/{id}/commission-runs` — every calculation run ever recorded for this
 * contract, newest first. Never empty-vs-error ambiguous: an empty array means "no calculation has
 * ever been saved for this contract yet", not a failure. */
export function fetchCommissionRuns(contractId: number): Promise<CommissionRunSummary[]> {
  return api<CommissionRunSummary[]>(`/api/contracts/${contractId}/commission-runs`)
}

/**
 * TASK-0268. One beneficiary line of `GET /api/commission-runs/{runId}`'s detail — the "perché
 * questo importo" this card exists for: base, gross percentage, net (applied) percentage, role,
 * level and the source of that percentage, all in one place. `grossPercentage`/`appliedPercentage`/
 * `amount` are ABSENT (not null) for a caller without `CanSeeRealAmounts`.
 */
export interface CommissionRunLine {
  beneficiaryUserId: number
  beneficiaryKind: string
  level: number
  commissionRoleId: number
  roleName: string | null
  grossPercentage?: number | null
  appliedPercentage?: number
  amount?: number
  percentageSource: string
  competenceDate: string
  isCurrent: boolean
}

/** `GET /api/commission-runs/{runId}`'s full response: the run's own header (same shape as
 * `CommissionRunSummary`) plus `lines`, one per beneficiary, ordered by chain level. */
export interface CommissionRunDetail {
  header: CommissionRunSummary
  lines: CommissionRunLine[]
}

/** `GET /api/commission-runs/{runId}`. Row-level visibility on the run's own contract applies (ADR-008):
 * an invisible/nonexistent run's contract gets a 404 the `api()` helper turns into a thrown `Error`. */
export function fetchCommissionRunDetail(runId: number): Promise<CommissionRunDetail> {
  return api<CommissionRunDetail>(`/api/commission-runs/${runId}`)
}

/**
 * TASK-0268. `POST /api/contracts/{id}/commission-runs`'s response — ALWAYS a `200`/`201` for a
 * visible, in-scope contract (this module's own "diagnostic outcome inside a 2xx, never a 4xx for a
 * merely-not-computable state" convention, same as `CommissionPreview`). `computable: false` means
 * nothing was written (see `outcome`/`note`); `computable: true, unchanged: true` means the input
 * fingerprint matched the contract's most recent run — no new row, `runId` names the existing one;
 * `computable: true, unchanged: false` means a NEW run (and its allocations) were actually persisted,
 * with every scalar the new run's own header would carry.
 */
export interface CommissionRunCreateResult {
  contractId: number
  computable: boolean
  unchanged: boolean
  outcome?: string
  note?: string
  runId?: number
  runAt?: string
  planDate?: string
  basisSource?: string
  basisAmount?: number
  totalPercentage?: number
  totalAmount?: number
  companyResidual?: number
  truncation?: string | null
  allocationsCount?: number
}

/**
 * TASK-0268. `POST /api/contracts/{id}/commission-runs` — "Calcola e salva", Admin-only server-side
 * (`PolicyNames.Admin`); a non-Admin caller gets a 403 the `api()` helper turns into a thrown
 * `Error`. On a lost race against a genuinely concurrent second POST for the same contract, the
 * backend answers 409 the same way.
 */
export function createCommissionRun(contractId: number): Promise<CommissionRunCreateResult> {
  return api<CommissionRunCreateResult>(`/api/contracts/${contractId}/commission-runs`, {
    method: 'POST',
  })
}

/* ───────────────────────── F-070 (TASK-0324): proposte di modifica al listino ───────────────────────── */

/** Stati del ciclo BOZZA → TEST → SIMULAZIONE → APPROVAZIONE → PUBBLICAZIONE (+ Scartata). */
export type CommissionProposalStatus =
  | 'Bozza'
  | 'Verificata'
  | 'Simulata'
  | 'Approvata'
  | 'Pubblicata'
  | 'Scartata'

export interface CommissionProposalSummary {
  id: number
  title: string
  reason: string
  validFrom: string
  status: CommissionProposalStatus
  createdBy: number | null
  createdAt: string
  updatedAt: string | null
  linesCount: number
}

export interface CommissionProposalLine {
  departmentId: number
  commissionRoleId: number
  percentage: number
  /** Percentuale in vigore oggi per la cella (null se la cella non ha ancora una versione aperta). */
  currentPercentage?: number | null
}

export interface CommissionProposalIssue {
  code: string
  message: string
  departmentId?: number | null
  commissionRoleId?: number | null
}

export interface CommissionProposalTestResult {
  passed: boolean
  testedAt?: string
  issues: CommissionProposalIssue[]
}

export interface CommissionSimulationAgentRow {
  userId: number
  oldAmount: number
  newAmount: number
  difference: number
  contracts: number
}

export interface CommissionSimulationContractRow {
  contractId: number
  agentId: number | null
  departmentId: number
  referenceDate: string
  basis: number
  oldTotal: number
  newTotal: number
  difference: number
}

/** Esito della simulazione su dati storici (§219). Gli importi sono visibili solo all'Admin. */
export interface CommissionSimulation {
  window: { from: string; to: string }
  statuses: string[]
  oldTotal: number
  newTotal: number
  difference: number
  contractsConsidered: number
  contractsComputed: number
  contractsAffected: number
  agentsAffected: number
  contractsWithoutBasis: number
  contractsWithoutAgent: number
  contractsWithoutChain: number
  basisSources: Record<string, number>
  perAgent: CommissionSimulationAgentRow[]
  sample: CommissionSimulationContractRow[]
  fingerprint: string
}

export interface CommissionProposalDetail extends Omit<CommissionProposalSummary, 'linesCount'> {
  testedAt: string | null
  testedBy: number | null
  test: CommissionProposalTestResult | null
  simulatedAt: string | null
  simulatedBy: number | null
  simulation: CommissionSimulation | null
  simulationFingerprint: string | null
  approvedBy: number | null
  approvedAt: string | null
  publishedBy: number | null
  publishedAt: string | null
  discardReason: string | null
  lines: CommissionProposalLine[]
}

export interface CommissionProposalRequest {
  title: string
  reason: string
  validFrom: string
  lines: { departmentId: number; commissionRoleId: number; percentage: number }[]
}

export function fetchCommissionProposals(status?: CommissionProposalStatus): Promise<CommissionProposalSummary[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return api<CommissionProposalSummary[]>(`/api/commission-plan-rate-proposals${qs}`)
}

export function fetchCommissionProposal(id: number): Promise<CommissionProposalDetail> {
  return api<CommissionProposalDetail>(`/api/commission-plan-rate-proposals/${id}`)
}

/** Crea una proposta (Bozza). Mai operativa prima della pubblicazione. */
export function createCommissionProposal(req: CommissionProposalRequest): Promise<CommissionProposalDetail> {
  return api<CommissionProposalDetail>('/api/commission-plan-rate-proposals', {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

/** Modifica una proposta non terminale: torna a Bozza e azzera test, simulazione e approvazione. */
export function updateCommissionProposal(
  id: number,
  req: CommissionProposalRequest,
): Promise<CommissionProposalDetail> {
  return api<CommissionProposalDetail>(`/api/commission-plan-rate-proposals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  })
}

export function testCommissionProposal(
  id: number,
): Promise<{ passed: boolean; status: CommissionProposalStatus; issues: CommissionProposalIssue[] }> {
  return api(`/api/commission-plan-rate-proposals/${id}/test`, { method: 'POST' })
}

/** Dry-run su contratti storici; finestra massima 24 mesi, stati di default "Attivata". */
export function simulateCommissionProposal(
  id: number,
  req: { from: string; to: string; statuses?: string[] },
): Promise<{ id: number; status: CommissionProposalStatus; simulation: CommissionSimulation }> {
  return api(`/api/commission-plan-rate-proposals/${id}/simulate`, {
    method: 'POST',
    body: JSON.stringify(req),
  })
}

/** Four-eyes: l'approvatore deve essere diverso dall'autore (altrimenti 409). */
export function approveCommissionProposal(id: number): Promise<CommissionProposalDetail> {
  return api<CommissionProposalDetail>(`/api/commission-plan-rate-proposals/${id}/approve`, { method: 'POST' })
}

export function publishCommissionProposal(id: number): Promise<{ id: number; status: CommissionProposalStatus }> {
  return api(`/api/commission-plan-rate-proposals/${id}/publish`, { method: 'POST' })
}

export function discardCommissionProposal(id: number, reason: string): Promise<{ id: number; status: CommissionProposalStatus }> {
  return api(`/api/commission-plan-rate-proposals/${id}/discard`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

/** Un periodo di `GET /api/commissions/series`; gli importi sono `null` per chi non vede le provvigioni totali. */
export interface CommissionsSeriesPoint {
  start: string
  end: string
  presunte: number | null
  maturate: number | null
  pagate: number | null
}

export interface CommissionsSeries {
  groupBy?: 'day' | 'week' | 'month'
  points: CommissionsSeriesPoint[]
}

export function fetchCommissionsSeries(from: string, to: string, groupBy?: 'day' | 'week' | 'month'): Promise<CommissionsSeries> {
  const p = new URLSearchParams({ from, to })
  if (groupBy) p.set('groupBy', groupBy)
  return api<CommissionsSeries>(`/api/commissions/series?${p.toString()}`)
}

/** `GET /api/operator-commissions/monthly`. Gli importi sono null per chi non puo' vederli. */
export interface OperatorPayment {
  id: number
  amount: number
  paidAt: string
  method: string | null
  /** Interna: null per chi non e' del personale. */
  note: string | null
}

export interface OperatorMonthlyShare {
  beneficiary: string
  userId: number | null
  gross: number | null
  net: number | null
  paid: number
  /** netto - pagato (negativo se pagato di piu'). */
  remaining: number | null
  payments: OperatorPayment[]
}

export interface OperatorMonthlySummary {
  month: string
  operator: string
  grossTotal: number | null
  invoiceTotal: number | null
  rowsTotal: number | null
  unexplained: number | null
  shares: OperatorMonthlyShare[]
  operatorId?: number | null
  /** 'statement' = estratto PDF, 'payments' = pagamenti operatore, 'none' = nessun dato. */
  source?: 'statement' | 'payments' | 'none'
}

export interface OperatorOption { id: number; name: string }

export interface OperatorMonthlyResponse {
  months: OperatorMonthlySummary[]
  operators?: OperatorOption[]
  /** True solo per chi puo' registrare/eliminare pagamenti (Admin). */
  canRecordPayments?: boolean
}

export function fetchOperatorMonthly(
  from: string, to: string, opts: { operatorId?: number | undefined; includeEmpty?: boolean | undefined } = {},
): Promise<OperatorMonthlyResponse> {
  const qs = new URLSearchParams({ from, to })
  if (opts.operatorId) qs.set('operatorId', String(opts.operatorId))
  if (opts.includeEmpty) qs.set('includeEmpty', 'true')
  return api<OperatorMonthlyResponse>(`/api/operator-commissions/monthly?${qs.toString()}`)
}

export interface RegisterOperatorPaymentRequest {
  month: string
  beneficiary: string
  operatorId?: number | undefined
  amount: number
  paidAt: string
  method?: string | undefined
  note?: string | undefined
}

/** `POST /api/operator-commissions/payments` (Admin). */
export function registerOperatorPayment(req: RegisterOperatorPaymentRequest): Promise<{ id: number }> {
  return api<{ id: number }>('/api/operator-commissions/payments', { method: 'POST', body: JSON.stringify(req) })
}

/** `DELETE /api/operator-commissions/payments/{id}` (Admin). */
export function deleteOperatorPayment(id: number): Promise<void> {
  return api<void>(`/api/operator-commissions/payments/${id}`, { method: 'DELETE' })
}
